import React, { useEffect, useState } from 'react';
import { Breadcrumb, Button, Card, Col, Form, Input, Row, Select, Steps, Switch } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getClasses } from 'app/entities/classe/classe.reducer';
import { getEcole } from '../ecole/ecole.reducer';
import { createEleve, getEleve, getEleveInscriptions, reset, updateEleve } from './eleve.reducer';
import { Link, RouteComponentProps } from 'react-router-dom';
import { getEcoleAnneeScolaire } from '../annee-scolaire/annee-scolaire.reducer';
import { StatusAnneeScolaire } from 'app/shared/model/anneeScolaire.model';
import { getParents } from '../parent/parent.reducer';
import { IUserParent } from 'app/shared/model/user-parent.model';

const { Option } = Select;

export const EleveUpdate = (props: RouteComponentProps<{ matricule: string; ecoleid: string; id: string; anneeScolaireId: string }>) => {
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const classes = useAppSelector(state => state.classe.entities);
  const eleve = useAppSelector(state => state.eleve.entity);
  const updateSuccess = useAppSelector(state => state.eleve.updateSuccess);
  const currentEcole = useAppSelector(state => state.ecole.entity);
  const anneeScolaire = props.match.params.anneeScolaireId;
  const [shouldCreateInscription, setShouldCreateInscription] = useState(false);
  const [updateInscription, setUpdateInscription] = useState(false);
  const anneeScolaireList = useAppSelector(state => state.anneeScolaire.entities);
  const inscriptions = useAppSelector(state => state.eleve.entitiesInscriptions);
  const parents = useAppSelector(state => state.parent.userParents);
  const [selectedParent, setSelectedParent] = useState<IUserParent | null>(null);

  const handleClose = event => {
    event.stopPropagation();
    props.history.push(`/eleve/${currentEcole && currentEcole.id}`);
  };

  useEffect(() => {
    dispatch(getClasses(props.match.params.ecoleid));
    dispatch(getEcole(props.match.params.ecoleid));
    dispatch(getEcoleAnneeScolaire(props.match.params.ecoleid));
    dispatch(getParents({ ecoleId: props.match.params.ecoleid, page: 0, size: 2000 }));
  }, []);

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEleve(props.match.params.id));
      dispatch(getEleveInscriptions(props.match.params.id));
    }
  }, [props.match.params.id]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose(event);
    }
  }, [updateSuccess]);

  const next = async () => {
    try {
      await form.validateFields(['matricule', 'firstName', 'lastName', 'montantScolarite']);
      setCurrentStep(currentStep + 1);
    } catch (error) {
      // Intentionnellement vide : la validation empêche d'avancer si erreurs
    }
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleChange = async checked => {
    try {
      await form.validateFields([
        ['parent', 'firstName'],
        ['parent', 'lastName'],
        ['parent', 'telephone'],
      ]);
      if (isNew) {
        setShouldCreateInscription(checked);
      } else setUpdateInscription(checked);
      if (checked) {
        setCurrentStep(2); // Passe directement à l'étape 3 (index 2)
      } else {
        setCurrentStep(1); // Reviens à l'étape 2 si décoché
      }
    } catch (error) {
      // Intentionnellement vide : la validation empêche d’avancer si erreurs
    }
  };

  const handleFinish = values => {
    const entity = {
      ...eleve,
      ...values,
      ecoleId: props.match.params.ecoleid,
      createInscription: shouldCreateInscription,
      updateInscription,
      parent: selectedParent || values.parent,
      montantInscription: values.montantInscription,
    };

    if (isNew) {
      dispatch(createEleve(entity));
    } else {
      dispatch(updateEleve(entity));
    }
  };

  return (
    <div>
      <Breadcrumb className="p-2">
        <Breadcrumb.Item>
          <Link to={`/home/${currentEcole?.id}/${anneeScolaire}`}>{currentEcole?.nom}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/eleve/${currentEcole?.id}/${anneeScolaire}`}>Élèves</Link>
        </Breadcrumb.Item>
        {!isNew && (
          <Breadcrumb.Item>
            <Link to={props.match.url}>Détails</Link>
          </Breadcrumb.Item>
        )}
      </Breadcrumb>

      <Card>
        <Row className="justify-content-center">
          <Col span={16}>
            <h2>{isNew ? 'Créer un élève' : 'Modifier un élève'}</h2>
            <Steps current={currentStep} className="custom-steps">
              <Steps.Step title="Élève" />
              <Steps.Step title="Parent" />
              <Steps.Step title="Inscription" />
            </Steps>

            <Form layout="vertical" form={form} initialValues={isNew ? {} : eleve} onFinish={handleFinish} style={{ marginTop: 24 }}>
              {/* Étape Élève */}
              <Form.Item name="matricule" label="Matricule" rules={[{ required: true }]} hidden={currentStep !== 0}>
                <Input placeholder="Matricule élève" />
              </Form.Item>
              <Form.Item name="firstName" label="Prénom" rules={[{ required: true }]} hidden={currentStep !== 0}>
                <Input placeholder="Prénom" />
              </Form.Item>
              <Form.Item name="lastName" label="Nom" rules={[{ required: true }]} hidden={currentStep !== 0}>
                <Input placeholder="Nom" />
              </Form.Item>
              <Form.Item name="montantScolarite" label="Montant Scolarité" rules={[{ required: true }]} hidden={currentStep !== 0}>
                <Input />
              </Form.Item>

              {/* Étape Parent */}
              <Form.Item
                name={['parent', 'telephone']}
                label="Téléphone du parent"
                rules={[{ required: true, message: 'Ce champ doit contenir au maximum 14 chiffres' }]}
                hidden={currentStep !== 1}
              >
                <Input
                  maxLength={14}
                  onChange={e => {
                    const phoneNumber = e.target.value;
                    const existingParent = parents.find(p => p.telephone?.toString() === phoneNumber);
                    if (existingParent) {
                      form.setFieldsValue({
                        parent: {
                          id: existingParent.parentId,
                          firstName: existingParent.firstName,
                          lastName: existingParent.lastName,
                          telephone: existingParent.telephone,
                        },
                      });
                      setSelectedParent(existingParent);
                    } else {
                      form.setFieldsValue({
                        parent: {
                          id: undefined,
                          firstName: undefined,
                          lastName: undefined,
                        },
                      });
                      setSelectedParent(null);
                    }
                  }}
                />
              </Form.Item>
              <Form.Item name={['parent', 'firstName']} label="Prénom du parent" rules={[{ required: true }]} hidden={currentStep !== 1}>
                <Input disabled={!!selectedParent} />
              </Form.Item>
              <Form.Item name={['parent', 'lastName']} label="Nom du parent" rules={[{ required: true }]} hidden={currentStep !== 1}>
                <Input disabled={!!selectedParent} />
              </Form.Item>

              {/* Étape Inscription */}
              {isNew ? (
                <Form.Item label="Voulez-vous inscrire l'élève ?" valuePropName="checked" hidden={currentStep !== 2}>
                  <span>
                    Si vous selectionner cette option, l&apos;élève sera automatiquement inscrit dans l&apos;année scolaire séléctionnée.
                  </span>
                  <br />
                  <Switch checked={shouldCreateInscription} onChange={checked => handleChange(checked)} />
                </Form.Item>
              ) : (
                <Form.Item label="Voulez-vous modifier l'inscription" valuePropName="checked" hidden={currentStep !== 2}>
                  <span>
                    Cette option permet de mettre à jour la classe de l&apos;élève déja INSCRIT. Si vous souhaitez changer l&apos;année
                    scolaire de l&apos;élève, veuillez accéder à sa page d&apos;inscription pour le réinscrire.
                  </span>
                  <br />
                  <Switch checked={updateInscription} onChange={checked => handleChange(checked)} />
                </Form.Item>
              )}
              {(shouldCreateInscription || updateInscription) && (
                <>
                  <Form.Item
                    label="Annee Scolaire"
                    name="anneeScolaireId"
                    rules={[{ required: shouldCreateInscription, message: "L'année scolaire est obligatoire" }]}
                    hidden={currentStep !== 2}
                  >
                    <Select placeholder="Liste des années scolaires" optionFilterProp="children" allowClear>
                      {anneeScolaireList
                        .filter(as => as.statutAnneeScolaire !== StatusAnneeScolaire.CLOTUREE)
                        .map(anneeSco => (
                          <Select.Option
                            key={anneeSco.id}
                            value={anneeSco.id}
                            disabled={inscriptions?.some(inscription => inscription.anneeScolaireId === anneeSco.id) || false}
                          >
                            {anneeSco.nom}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name={['classe', 'id']}
                    label="Classe"
                    rules={[{ required: shouldCreateInscription }]}
                    hidden={currentStep !== 2}
                  >
                    <Select placeholder="Classe">
                      {classes.map(classe => (
                        <Option key={classe.id} value={classe.id}>
                          {classe.nom}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="montantInscription"
                    hidden={currentStep !== 2}
                    label="Montant d'inscription"
                    rules={[{ required: shouldCreateInscription }]}
                  >
                    <Input />
                  </Form.Item>
                </>
              )}

              {/* Boutons navigation */}
              <Form.Item>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  {(currentStep === 1 || currentStep === 2) && (
                    <Button style={{ backgroundColor: '#F28E19', color: 'white' }} onClick={prev}>
                      Précédent
                    </Button>
                  )}
                  {(currentStep === 0 || currentStep === 1) && (
                    <Button style={{ backgroundColor: '#F28E19', color: 'white' }} onClick={next}>
                      Suivant
                    </Button>
                  )}
                  {currentStep === 2 && (
                    <Button
                      style={{ backgroundColor: '#F28E19', color: 'white' }}
                      data-cy="entityCreateSaveButton"
                      htmlType="submit"
                      id="save-entity"
                    >
                      Enregistrer
                    </Button>
                  )}
                </div>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default EleveUpdate;
